import React, { useState, useEffect } from 'react';
import AdminNav from './AdminNav';
import './AdminLayout.css';
import './AdminTable.css';

import { API, authHeadersOnly } from '../services/apiConfig';

function AdminReviews() {
  const [reviews, setReviews]       = useState([]);
  const [feedbacks, setFeedbacks]   = useState([]);
  const [loading, setLoading]       = useState(true);
  const [ratingFilter, setRatingFilter] = useState('ALL');
  const [activeTab, setActiveTab]   = useState('reviews');

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    setLoading(true);
    const [rvRes, fbRes] = await Promise.all([
      fetch(`${API}/api/orders/product-reviews`, { headers: authHeadersOnly() }).then(r => r.ok ? r.json() : []).catch(() => []),
      fetch(`${API}/api/orders/feedback/all`, { headers: authHeadersOnly() }).then(r => r.ok ? r.json() : []).catch(() => []),
    ]);
    setReviews(Array.isArray(rvRes) ? rvRes : []);
    setFeedbacks(Array.isArray(fbRes) ? fbRes : []);
    setLoading(false);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.href = '/login';
  };

  const renderStars = (rating) =>
    [...Array(5)].map((_, i) => (
      <span key={i} style={{ color: i < parseInt(rating) ? '#ffc107' : '#ddd', fontSize: '16px' }}>★</span>
    ));

  const avgOf = (list) =>
    list.length ? (list.reduce((s, r) => s + parseInt(r.rating || 0), 0) / list.length).toFixed(1) : '—';

  const activeList = activeTab === 'reviews' ? reviews : feedbacks;
  const filtered   = activeList.filter(r => ratingFilter === 'ALL' || parseInt(r.rating) === parseInt(ratingFilter));

  const resolveUser = (item) => {
    const name = item.userName && item.userName.trim() && item.userName !== 'null' && item.userName !== 'Deleted User'
      ? item.userName
      : null;
    return name || item.userEmail || `User #${item.userId}`;
  };

  return (
    <>
      <AdminNav onLogout={handleLogout} />
      <div className="admin-page-wrapper">
        <div className="admin-page-container">

          <div className="admin-page-header">
            <h1 className="admin-page-title">Ratings & Reviews</h1>
            <p className="admin-page-subtitle">Product reviews from product pages · Order feedback submitted after placing orders</p>
          </div>

          {/* Summary cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '24px' }}>
            {[
              { label: 'Product Reviews', value: reviews.length,   color: '#ff0000' },
              { label: 'Order Feedback',  value: feedbacks.length, color: '#e47911' },
              { label: 'Avg (Reviews)',   value: avgOf(reviews)  + ' ★', color: '#067d62' },
              { label: 'Avg (Feedback)',  value: avgOf(feedbacks) + ' ★', color: '#6366f1' },
            ].map((card) => (
              <div key={card.label} style={{ background: 'white', borderRadius: '8px', padding: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', border: `1px solid #e0e0e0`, borderTop: `4px solid ${card.color}` }}>
                <div style={{ fontSize: '28px', fontWeight: '800', color: '#232f3e' }}>{card.value}</div>
                <div style={{ fontSize: '13px', color: '#888', marginTop: '4px' }}>{card.label}</div>
              </div>
            ))}
          </div>

          {/* Tabs + filter */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={() => { setActiveTab('reviews'); setRatingFilter('ALL'); }}
                style={{ padding: '8px 20px', borderRadius: '20px', border: 'none', cursor: 'pointer', fontWeight: '600', fontSize: '14px',
                  background: activeTab === 'reviews' ? '#9A1111' : '#f0f0f0',
                  color:      activeTab === 'reviews' ? 'white'   : '#555' }}>
                Product Reviews ({reviews.length})
              </button>
              <button
                onClick={() => { setActiveTab('feedback'); setRatingFilter('ALL'); }}
                style={{ padding: '8px 20px', borderRadius: '20px', border: 'none', cursor: 'pointer', fontWeight: '600', fontSize: '14px',
                  background: activeTab === 'feedback' ? '#9A1111' : '#f0f0f0',
                  color:      activeTab === 'feedback' ? 'white'   : '#555' }}>
                Order Feedback ({feedbacks.length})
              </button>
            </div>
            <select value={ratingFilter} onChange={e => setRatingFilter(e.target.value)} className="admin-filter-select">
              <option value="ALL">All Ratings</option>
              {[5,4,3,2,1].map(r => <option key={r} value={r}>{r} Star{r !== 1 ? 's' : ''}</option>)}
            </select>
          </div>

          {loading ? (
            <div style={{ textAlign: 'center', padding: '60px', color: '#888' }}>Loading...</div>
          ) : (
            <div className="admin-table-card">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>User</th>
                    {activeTab === 'reviews' ? <th>Product ID</th> : <th>Order ID</th>}
                    <th>Rating</th>
                    <th>{activeTab === 'reviews' ? 'Review' : 'Comment'}</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 ? (
                    <tr><td colSpan="6" style={{ textAlign: 'center', padding: '40px', color: '#888' }}>No entries found</td></tr>
                  ) : (
                    filtered.map((item, idx) => (
                      <tr key={item.id || idx}>
                        <td style={{ color: '#888', fontSize: '13px' }}>{filtered.length - idx}</td>
                        <td>
                          <div style={{ fontWeight: '600', color: '#232f3e' }}>{resolveUser(item)}</div>
                          {item.userEmail && item.userEmail.trim() && (
                            <div style={{ fontSize: '12px', color: '#888', marginTop: '2px' }}>{item.userEmail}</div>
                          )}
                        </td>
                        <td style={{ fontWeight: '600' }}>
                          {activeTab === 'reviews' ? (
                            item.productCustomId
                              ? <span style={{ fontSize: '12px', fontWeight: '700', color: '#9A1111', background: '#fff5f5', border: '1px solid #fecaca', borderRadius: '5px', padding: '2px 8px', display: 'inline-block' }}>{item.productCustomId}</span>
                              : <span style={{ color: '#888' }}>#{item.productId}</span>
                          ) : (
                            <><span style={{ color: '#888' }}>#{item.orderId}</span></>
                          )}
                        </td>

                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            {renderStars(item.rating)}
                            <span style={{ fontWeight: '700', color: '#232f3e', marginLeft: '4px' }}>{parseInt(item.rating)}/5</span>
                          </div>
                        </td>
                        <td style={{ maxWidth: '300px', color: '#555' }}>
                          {(activeTab === 'reviews' ? item.reviewText : item.comment)
                            || <span style={{ color: '#bbb', fontStyle: 'italic' }}>No comment</span>}
                        </td>
                        <td style={{ color: '#888', fontSize: '13px' }}>
                          {item.createdAt ? new Date(item.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}

        </div>
      </div>
    </>
  );
}

export default AdminReviews;
